import React, { useState, useEffect } from 'react'
import { Table, Button, Modal, Form, Input, Select, message, Tag, Space, Card, InputNumber, Divider } from 'antd'
import { PlusOutlined, DeleteOutlined, CheckCircleOutlined, CloudServerOutlined, ApiOutlined, RocketOutlined, GoogleOutlined, SendOutlined, EditOutlined } from '@ant-design/icons'
import { smtpApi, SMTPAccount } from '../../services/emailApi'

const publicBaseUrl = (import.meta.env.VITE_PUBLIC_BASE_URL || window.location.origin).replace(/\/$/, '')

const SMTPSettings: React.FC<{ embedded?: boolean }> = ({ embedded }) => {
    const [accounts, setAccounts] = useState<SMTPAccount[]>([])
    const [loading, setLoading] = useState(false)
    const [isModalVisible, setIsModalVisible] = useState(false)
    const [editingId, setEditingId] = useState<number | null>(null)
    const [form] = Form.useForm()
    const [testing, setTesting] = useState(false)

    const fetchAccounts = async () => {
        setLoading(true)
        try {
            const res = await smtpApi.getAll()
            setAccounts(res.data)
        } catch (error) {
            message.error('Не удалось загрузить SMTP аккаунты')
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        fetchAccounts()
    }, [])

    const handleDelete = async (id: number) => {
        try {
            await smtpApi.delete(id)
            message.success('Аккаунт удален')
            fetchAccounts()
        } catch (error) {
            message.error('Ошибка удаления')
        }
    }

    const openCreateModal = () => {
        setEditingId(null)
        form.resetFields()
        setIsModalVisible(true)
    }

    const handleEdit = (record: SMTPAccount) => {
        setEditingId(record.id)
        form.setFieldsValue({
            ...record,



        })
        setIsModalVisible(true)
    }

    const handleCreateOrUpdate = async (values: any) => {
        try {
            if (editingId) {
                await smtpApi.update(editingId, values)
                message.success('Аккаунт обновлен')
            } else {
                await smtpApi.create(values)
                message.success('Аккаунт создан')
            }
            setIsModalVisible(false)
            form.resetFields()
            setEditingId(null)
            fetchAccounts()
        } catch (error: any) {
            message.error('Ошибка: ' + (error.response?.data?.detail || error.message))
        }
    }

    const [testEmailModalVisible, setTestEmailModalVisible] = useState(false)
    const [testEmailAccount, setTestEmailAccount] = useState<number | null>(null)
    const [testEmailForm] = Form.useForm()

    const handleTestClick = (id: number) => {
        setTestEmailAccount(id)
        setTestEmailModalVisible(true)
    }

    const handleTestSubmit = async (values: any) => {
        if (!testEmailAccount) return

        setTesting(true)
        try {
            await smtpApi.test(testEmailAccount, values.email)
            message.success('Тестовое письмо успешно отправлено!')
            setTestEmailModalVisible(false)
            testEmailForm.resetFields()
        } catch (error: any) {
            message.error(`Ошибка проверки: ${error.response?.data?.detail || error.message}`)
        } finally {
            setTesting(false)
        }
    }

    const applyPreset = (preset: string) => {
        switch (preset) {
            case 'aws':
                form.setFieldsValue({
                    name: "Amazon SES",
                    host: "email-smtp.us-east-1.amazonaws.com",
                    port: 587,
                    security: "starttls",
                    daily_limit: 50000
                })
                break
            case 'sendgrid':
                form.setFieldsValue({
                    name: "SendGrid",
                    host: "smtp.sendgrid.net",
                    port: 587,
                    security: "starttls",
                    daily_limit: 100
                })
                break
            case 'mailgun':
                form.setFieldsValue({
                    name: "Mailgun",
                    host: "smtp.mailgun.org",
                    port: 587,
                    security: "starttls",
                    daily_limit: 1000
                })
                break
            case 'gmail':
                form.setFieldsValue({
                    name: "Gmail",
                    host: "smtp.gmail.com",
                    port: 587,
                    security: "starttls",
                    daily_limit: 500
                })
                message.info("Для Gmail используйте 'Пароль приложения', если включена 2FA.")
                break
        }
    }

    const columns = [
        { title: 'Название', dataIndex: 'name', key: 'name', render: (text: string) => <b>{text}</b> },
        { title: 'Хост', dataIndex: 'host', key: 'host' },
        {
            title: 'От кого',
            key: 'from',
            render: (_: any, r: SMTPAccount) => `${r.from_name || ''} <${r.from_email}>`
        },
        {
            title: 'Лимит/день',
            dataIndex: 'daily_limit',
            key: 'daily_limit',
            render: (val: number) => <Tag color="blue">{val}</Tag>
        },
        {
            title: 'Действия',
            key: 'actions',
            render: (_: any, record: SMTPAccount) => (
                <Space>
                    <Button
                        icon={<EditOutlined />}
                        onClick={() => handleEdit(record)}
                        size="small"
                    />
                    <Button
                        icon={<CheckCircleOutlined />}
                        onClick={() => handleTestClick(record.id)}
                        loading={testing && testEmailAccount === record.id}
                        size="small"
                    >
                        Проверить
                    </Button>
                    <Button
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => handleDelete(record.id)}
                        size="small"
                    />
                </Space>
            )
        }
    ]

    return (
        <div className={embedded ? '' : 'p-6'}>
            {!embedded && (
                <div className="flex justify-between items-center mb-6">
                    <div>
                        <h1 className="text-2xl font-bold">Настройки SMTP</h1>
                        <p className="text-gray-500">Управление почтовыми серверами (AWS SES, SendGrid, Gmail)</p>
                    </div>
                    <Button type="primary" icon={<PlusOutlined />} onClick={openCreateModal}>
                        Добавить SMTP сервер
                    </Button>
                </div>
            )}

            {embedded && (
                <div className="flex justify-end mb-4">
                    <Button type="primary" icon={<PlusOutlined />} onClick={openCreateModal}>
                        Добавить SMTP сервер
                    </Button>
                </div>
            )}

            <Card>
                <Table
                    columns={columns}
                    dataSource={accounts}
                    rowKey="id"
                    loading={loading}
                    pagination={false}
                    locale={{ emptyText: 'Нет настроенных SMTP серверов. Добавьте один для старта.' }}
                    scroll={{ x: 'max-content' }}
                />
            </Card>


            <Modal
                title="Отправка тестового письма"
                open={testEmailModalVisible}
                onCancel={() => setTestEmailModalVisible(false)}
                footer={null}
            >
                <Form form={testEmailForm} onFinish={handleTestSubmit} layout="vertical">
                    <Form.Item
                        name="email"
                        label="Кому отправить тест?"
                        rules={[{ required: true, type: 'email', message: 'Введите корректный email' }]}
                    >
                        <Input placeholder="your-email@example.com" />
                    </Form.Item>
                    <div className="flex justify-end gap-2">
                        <Button onClick={() => setTestEmailModalVisible(false)}>Отмена</Button>
                        <Button type="primary" htmlType="submit" loading={testing} icon={<SendOutlined />}>
                            Отправить
                        </Button>
                    </div>
                </Form>
            </Modal>

            <Modal
                title={editingId ? "Редактировать SMTP Сервер" : "Добавить SMTP Сервер"}
                open={isModalVisible}
                onCancel={() => setIsModalVisible(false)}
                footer={null}
                width={650}
            >
                <div className="mb-6">
                    <div className="text-gray-500 mb-2 text-xs uppercase font-bold tracking-wider">Быстрые пресеты</div>
                    <div className="grid grid-cols-2 gap-3">
                        <div
                            className="border p-3 rounded-lg cursor-pointer hover:bg-blue-50 hover:border-blue-300 transition-all flex items-center gap-3 group"
                            onClick={() => applyPreset('aws')}
                        >
                            <div className="bg-blue-100 p-2 rounded-full group-hover:bg-blue-200 transition-colors">
                                <CloudServerOutlined className="text-blue-600 text-lg" />
                            </div>
                            <div>
                                <div className="font-semibold text-sm text-gray-800">Amazon SES</div>
                                <div className="text-xs text-blue-600 font-medium">Порт 2587</div>
                            </div>
                        </div>

                        <div className="mt-4 p-3 bg-gray-50 border border-gray-200 rounded-md text-xs text-gray-600">
                            <strong>Настройка возвратов (Bounces):</strong><br />
                            Вставьте этот URL в настройки SNS Topic для <br />
                            <i>Bounce</i> и <i>Complaint</i> уведомлений:<br />
                            <code className="block mt-1 p-1 bg-white border border-gray-300 rounded select-all cursor-pointer" onClick={() => navigator.clipboard.writeText(`${publicBaseUrl}/api/webhooks/aws-ses`)}>
                                {publicBaseUrl}/api/webhooks/aws-ses
                            </code>
                        </div>

                        <div
                            className="border p-3 rounded-lg cursor-pointer hover:bg-purple-50 hover:border-purple-300 transition-all flex items-center gap-3 group"
                            onClick={() => applyPreset('sendgrid')}
                        >
                            <div className="bg-purple-100 p-2 rounded-full group-hover:bg-purple-200 transition-colors">
                                <ApiOutlined className="text-purple-600 text-lg" />
                            </div>
                            <div>
                                <div className="font-semibold text-sm text-gray-800">SendGrid</div>
                                <div className="text-xs text-gray-400">Легкий старт</div>
                            </div>
                        </div>

                        <div
                            className="border p-3 rounded-lg cursor-pointer hover:bg-red-50 hover:border-red-300 transition-all flex items-center gap-3 group"
                            onClick={() => applyPreset('mailgun')}
                        >
                            <div className="bg-red-100 p-2 rounded-full group-hover:bg-red-200 transition-colors">
                                <RocketOutlined className="text-red-600 text-lg" />
                            </div>
                            <div>
                                <div className="font-semibold text-sm text-gray-800">Mailgun</div>
                                <div className="text-xs text-gray-400">Большие объемы</div>
                            </div>
                        </div>

                        <div
                            className="border p-3 rounded-lg cursor-pointer hover:bg-orange-50 hover:border-orange-300 transition-all flex items-center gap-3 group"
                            onClick={() => applyPreset('gmail')}
                        >
                            <div className="bg-orange-100 p-2 rounded-full group-hover:bg-orange-200 transition-colors">
                                <GoogleOutlined className="text-orange-600 text-lg" />
                            </div>
                            <div>
                                <div className="font-semibold text-sm text-gray-800">Gmail</div>
                                <div className="text-xs text-gray-400">Личный email</div>
                            </div>
                        </div>
                    </div>
                </div>

                <Divider dashed />

                <Form form={form} layout="vertical" onFinish={handleCreateOrUpdate}>
                    <Form.Item name="name" label="Название (для себя)" rules={[{ required: true }]}>
                        <Input placeholder="Например: Мой AWS SES или Корпоративный Gmail" />
                    </Form.Item>

                    <div className="grid grid-cols-2 gap-4">
                        <Form.Item name="host" label="SMTP Хост" rules={[{ required: true }]}>
                            <Input placeholder="e.g. email-smtp.us-east-1.amazonaws.com" />
                        </Form.Item>
                        <Form.Item name="port" label="Порт" rules={[{ required: true }]}>
                            <InputNumber className="w-full" placeholder="587" />
                        </Form.Item>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <Form.Item name="username" label="Пользователь (API User)" rules={[{ required: true }]}>
                            <Input autoComplete="off" />
                        </Form.Item>
                        <Form.Item name="password" label="Пароль (API Key / App Password)" rules={[{ required: !editingId }]} help={editingId ? "Оставьте пустым, если не меняете" : undefined}>
                            <Input.Password autoComplete="new-password" placeholder={editingId ? "********" : ""} />
                        </Form.Item>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <Form.Item name="from_email" label="Email отправителя (Verified)" rules={[{ required: true, type: 'email' }]}>
                            <Input placeholder="info@yourcompany.com" />
                        </Form.Item>
                        <Form.Item name="from_name" label="Имя отправителя">
                            <Input placeholder="Моя Компания" />
                        </Form.Item>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <Form.Item name="security" label="Шифрование" initialValue="starttls">
                            <Select>
                                <Select.Option value="starttls">STARTTLS (587/2587)</Select.Option>
                                <Select.Option value="ssl">SSL/TLS (465)</Select.Option>
                                <Select.Option value="none">Без шифрования (25)</Select.Option>
                            </Select>
                        </Form.Item>
                        <Form.Item name="daily_limit" label="Лимит в день" initialValue={50000}>
                            <InputNumber className="w-full" />
                        </Form.Item>
                    </div>

                    <Form.Item className="flex justify-end mb-0 mt-4">
                        <Space>
                            <Button onClick={() => setIsModalVisible(false)}>
                                Отмена
                            </Button>
                            <Button type="primary" htmlType="submit">
                                {editingId ? 'Обновить' : 'Создать'}
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    )
}

export default SMTPSettings
